import { createAsyncThunk } from '@reduxjs/toolkit';
import { DocumentTemplateType } from '@/Enums/DocumentTemplateType';
import { DeleteDocumentTemplateResponse } from '@/Api/DocumentTemplates/Responses/DeleteDocumentTemplateResponse';
import { IPrinterInfo } from '@/Electron/services/PrintService/IPrintService';
import { documentStoreService, printService } from '@/Electron';
import { Nullable } from '@/Types';
import { IDocumentPrinterInfo } from '@/Electron/services/DocumentStoreService/IDocumentStoreService';
import { GetAllDocumentTemplateResponse } from '@/Api/DocumentTemplates/Responses/GetAllDocumentTemplateResponse';
import { DocumentTemplateApi } from '@/Api/DocumentTemplates/DocumentTemplatesApi';
import { UpdateDocumentTemplateResponse } from '@/Api/DocumentTemplates/Responses/UpdateDocumentTemplateResponse';
import { UpdateDocumentTemplateRequest } from '@/Api/DocumentTemplates/Requests/UpdateDocumentTemplateRequest';
import { RestoreTemplateResponse } from '@/Api/DocumentTemplates/Responses/RestoreTemplateResponse';
import { RestoreTemplateRequest } from '@/Api/DocumentTemplates/Requests/RestoreTemplateRequest';
import { CopyTemplateResponse } from '@/Api/DocumentTemplates/Responses/CopyTemplateResponse';
import { CopyTemplateRequest } from '@/Api/DocumentTemplates/Requests/CopyTemplateRequest';

const getAllDocumentTemplate = createAsyncThunk<GetAllDocumentTemplateResponse>(
  'documentTemplateSettings/getAll',
  async () => {
    return DocumentTemplateApi.getAllDocumentTemplates(DocumentTemplateType.Document);
  },
);

const updateDocumentTemplate = createAsyncThunk<UpdateDocumentTemplateResponse, UpdateDocumentTemplateRequest>(
  'documentTemplateSettings/update',
  async (request) => {
    return DocumentTemplateApi.updateDocumentTemplate(request);
  },
);

const deleteDocumentTemplate = createAsyncThunk<DeleteDocumentTemplateResponse, string>(
  'documentTemplateSettings/delete',
  async (guid) => {
    return DocumentTemplateApi.deleteDocumentTemplate(guid);
  },
);

const restoreDocumentTemplate = createAsyncThunk<RestoreTemplateResponse, RestoreTemplateRequest>(
  'documentTemplateSettings/restore',
  async (request) => {
    return DocumentTemplateApi.restoreDocumentTemplate(request);
  },
);

const getAllPrinters = createAsyncThunk<IPrinterInfo[]>('documentTemplateSettings/getPrinters', async () => {
  return printService.getPrinters();
});

const getDefaultPrinter = createAsyncThunk<Nullable<IPrinterInfo>>(
  'documentTemplateSettings/getDefaultPrinter',
  async () => {
    return printService.getDefaultPrinter();
  },
);

const getSavedPrinterInfo = createAsyncThunk<Nullable<IDocumentPrinterInfo>, string>(
  'documentTemplateSettings/getSavedPrinterInfo',
  async (guid) => {
    return documentStoreService.getByDocumentGuid(guid);
  },
);

const savePrinterInfo = createAsyncThunk<IDocumentPrinterInfo, IDocumentPrinterInfo>(
  'documentTemplateSettings/savePrinterInfoRequest',
  async (documentPrinterInfo) => {
    documentStoreService.addOrUpdate(documentPrinterInfo);
    return documentPrinterInfo;
  },
);

const removePrinterInfo = createAsyncThunk(
  'documentTemplateSettings/removePrinterInfoRequest',
  async (documentGuid: string) => {
    documentStoreService.removeByDocumentGuid(documentGuid);
  },
);

const copyDocumentTemplate = createAsyncThunk<CopyTemplateResponse, CopyTemplateRequest>(
  'documentTemplateSettings/copy',
  async (request) => {
    return DocumentTemplateApi.copyDocumentTemplate(request);
  },
);

export const asyncActions = {
  getAllDocumentTemplate,
  deleteDocumentTemplate,
  updateDocumentTemplate,
  restoreDocumentTemplate,
  getAllPrinters,
  getDefaultPrinter,
  getSavedPrinterInfo,
  savePrinterInfo,
  removePrinterInfo,
  copyDocumentTemplate,
};
